import { useEffect, useRef } from 'react';

const HEX_RE = /^#[0-9a-f]{6}$/i;

function hexToHsl(hex) {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s, l];
}

function hslToHex(h, s, l) {
  const hh = ((h % 360) + 360) % 360;
  const a = s * Math.min(l, 1 - l);
  function f(n) {
    const k = (n + hh / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * Math.max(0, Math.min(1, c)))
      .toString(16)
      .padStart(2, '0');
  }
  return `#${f(0)}${f(8)}${f(4)}`;
}

function generateScale(hex) {
  const [h, s] = hexToHsl(hex);
  return {
    '--color-brand-900': hslToHex(h, s, 0.16),
    '--color-brand-800': hslToHex(h, s, 0.28),
    '--color-brand-700': hslToHex(h, s, 0.38),
    '--color-brand-600': hslToHex(h, s, 0.48),
    '--color-brand-500': hex,
    '--color-brand-100': hslToHex(h, s, 0.88),
    '--color-brand-50': hslToHex(h, s, 0.94),
    '--color-brand-deep': hslToHex(h, s, 0.10),
  };
}

const BRAND_PROPS = [
  '--color-brand-900',
  '--color-brand-800',
  '--color-brand-700',
  '--color-brand-600',
  '--color-brand-500',
  '--color-brand-100',
  '--color-brand-50',
  '--color-brand-deep',
];

export function useBranding(branding) {
  const appliedRef = useRef(false);

  useEffect(() => {
    const root = document.documentElement;
    const primary = branding?.primaryColor;

    if (primary && HEX_RE.test(primary)) {
      const scale = generateScale(primary);
      for (const [prop, value] of Object.entries(scale)) {
        root.style.setProperty(prop, value);
      }
      appliedRef.current = true;
    } else if (appliedRef.current) {
      for (const prop of BRAND_PROPS) {
        root.style.removeProperty(prop);
      }
      appliedRef.current = false;
    }

    return () => {
      if (appliedRef.current) {
        for (const prop of BRAND_PROPS) {
          root.style.removeProperty(prop);
        }
        appliedRef.current = false;
      }
    };
  }, [branding?.primaryColor]);
}
